import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import axios from 'axios';
import { useSetAtom } from 'jotai';
import {
  Button,
  Flex,
  HookFormErrorMessage,
  Input,
  Stack,
  Text,
  useToast,
} from '@pairy/pds-react';

import useSignUp from '@/hooks/mutations/auth/useSignUp';
import { userInfoAtom } from '@/state/user/atom';
import { ApiErrorResponse } from '@/types/api';
import { FORM_VALIDATIONS } from '@/constants/formValidations';

import CheckBox from '../CheckBox';

import * as Styled from './index.styles';
import LabelText from './LabelText';
import useCheckUserEmail from './useCheckUserEmail';

export interface Inputs {
  email: string;
  name: string;
  company: string;
  position: string;
  phoneNumber1: string;
  phoneNumber2: string;
  phoneNumber3: string;
  password: string;
  passwordCheck: string;
  isMarketingPolicyAccepted: boolean;
  isDefaultAgree: boolean;
  isTotalAgree: boolean;
  acquisitionPath: string;
  acquisitionPathEtc?: string;
}

const Form = () => {
  const router = useRouter();

  const {
    register,
    handleSubmit,
    watch,
    setFocus,
    setValue,
    getValues,
    formState: { errors, isValid },
  } = useForm<Inputs>({
    mode: 'onChange',
    defaultValues: {
      phoneNumber1: '010',
    },
  });

  const { mutateAsync } = useSignUp();
  const [signUpErrorMessage, setSignUpErrorMessage] = React.useState('');
  const setUserInfo = useSetAtom(userInfoAtom);

  const password = watch('password', '');
  const phoneNumber1 = watch('phoneNumber1', '010');
  const phoneNumber2 = watch('phoneNumber2', '');
  const acquisitionPath = watch('acquisitionPath', '');

  const {
    minutes,
    seconds,
    authCode,
    setAuthCode,
    errorMessage,
    authButtonMessage,
    isAuthCodeChecked,
    isAuthCodeClickable,
    isAuthCodeInputEditable,
    isAuthCodeInputVisible,
    handleAuthCodeConfirmClick,
    handleGetCodeButtonClick,
  } = useCheckUserEmail();

  const toast = useToast();

  React.useEffect(() => {
    if (phoneNumber1.length === 3) {
      setFocus('phoneNumber2');
    }
  }, [phoneNumber1]);

  React.useEffect(() => {
    if (phoneNumber2.length === 4) {
      setFocus('phoneNumber3');
    }
  }, [phoneNumber2]);

  React.useEffect(() => {
    setFocus('email');
  }, []);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    if (!isAuthCodeChecked) {
      toast.error('이메일 인증을 완료해 주세요');
      return;
    }
    const {
      email,
      name,
      company,
      position,
      acquisitionPath,
      acquisitionPathEtc,
    } = data;
    const request = {
      ...data,
      email: email.trim(),
      name: name.trim(),
      company: company.trim(),
      position: position.trim(),
      phone: `${data.phoneNumber1}${data.phoneNumber2}${data.phoneNumber3}`,
      acquisitionPath:
        acquisitionPath === '기타'
          ? `기타: ${acquisitionPathEtc?.trim() || ''}`
          : acquisitionPath,
    };

    try {
      const response = await mutateAsync(request);
      setUserInfo(response.data.data.user);
      setSignUpErrorMessage('');
      // 구글 애즈 전환 이벤트 트래킹
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          send_to: 'AW-17176230763/Qy-RCKOti9kaEOv2of4_',
        });
      }
      router.push('/user/pdf');
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        const { message } = error.response.data as ApiErrorResponse;
        setSignUpErrorMessage(message);
      }
    }
  };

  const getPhoneNumberErrorMessage = () => {
    if (errors.phoneNumber1) {
      return <HookFormErrorMessage errors={errors} name="phoneNumber1" />;
    }
    if (errors.phoneNumber2) {
      return <HookFormErrorMessage errors={errors} name="phoneNumber2" />;
    }
    if (errors.phoneNumber3) {
      return <HookFormErrorMessage errors={errors} name="phoneNumber3" />;
    }

    return null;
  };

  return (
    <Styled.Wrapper onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={16} direction="column">
        <div>
          <label>
            <LabelText>
              <Text variant="sm-medium">업무용 이메일 &nbsp;</Text>
              <Text variant="sm-medium" color="skyblue_500">
                *필수
              </Text>
            </LabelText>
            <Input
              type="text"
              placeholder="example@example.com"
              isError={Boolean(errors.email)}
              {...register('email', {
                required: FORM_VALIDATIONS.email.required,
                pattern: {
                  value: FORM_VALIDATIONS.email.pattern.value,
                  message: FORM_VALIDATIONS.email.pattern.message,
                },
              })}
              disabled={isAuthCodeChecked}
            />
          </label>
          <HookFormErrorMessage errors={errors} name="email" />
        </div>
        <Button
          type="button"
          variant="skyblue_400_fill"
          disabled={isAuthCodeChecked}
          onClick={() => {
            handleGetCodeButtonClick(getValues('email'));
          }}
        >
          {authButtonMessage}
        </Button>
      </Stack>

      {isAuthCodeInputVisible && (
        <Stack gap={16} direction="column">
          <label>
            <LabelText>
              <Text variant="sm-medium">인증코드 &nbsp;</Text>
            </LabelText>
            <Input
              type="string"
              placeholder="인증코드 6자리를 입력하세요"
              value={authCode}
              onChange={(e) => setAuthCode(e.target.value)}
              disabled={!isAuthCodeInputEditable}
            />
          </label>

          <Flex justifyContent="space-between" alignItems="center">
            <div>
              <Text color="zinc_400" variant="md-regular">
                {`${String(minutes).padStart(2, '0')} : ${String(
                  seconds,
                ).padStart(2, '0')}`}
              </Text>

              {!isAuthCodeChecked && errorMessage !== '' && (
                <Text color="cerise_300" variant="md-regular" paddingLeft={8}>
                  {errorMessage}
                </Text>
              )}
            </div>

            <Button
              type="button"
              variant="skyblue_400_fill"
              onClick={handleAuthCodeConfirmClick}
              disabled={!isAuthCodeClickable}
            >
              확인
            </Button>
          </Flex>
        </Stack>
      )}

      <div>
        <label>
          <LabelText>
            <Text variant="sm-medium">이름 혹은 닉네임 &nbsp;</Text>
            <Text variant="sm-medium" color="skyblue_500">
              *필수
            </Text>
          </LabelText>
          <Input
            type="text"
            placeholder="김유저"
            isError={Boolean(errors.name)}
            {...register('name', {
              required: '이름 혹은 닉네임을 입력해주세요.',
            })}
            maxLength={10}
          />
        </label>
        <HookFormErrorMessage errors={errors} name="name" />
      </div>

      <div>
        <label>
          <LabelText>
            <Text variant="sm-medium">비밀번호 &nbsp;</Text>
            <Text variant="sm-medium" color="skyblue_500">
              *필수
            </Text>
          </LabelText>
          <Input
            type="password"
            autoComplete="new-password"
            isError={Boolean(errors.password)}
            {...register('password', {
              required: '비밀번호를 입력해주세요.',
              minLength: {
                value: 8,
                message: '비밀번호는 최소 8자 이상 입력해주세요.',
              },
            })}
          />
        </label>
        <HookFormErrorMessage errors={errors} name="password" />
      </div>
      <div>
        <label>
          <LabelText>
            <Text variant="sm-medium">비밀번호 재확인 &nbsp;</Text>
            <Text variant="sm-medium" color="skyblue_500">
              *필수
            </Text>
          </LabelText>
          <Input
            type="password"
            autoComplete="new-password"
            isError={Boolean(errors.passwordCheck)}
            {...register('passwordCheck', {
              required: '비밀번호 재확인을 입력해주세요.',
              validate: (value) =>
                value === password || '비밀번호가 일치하지 않습니다.',
            })}
          />
        </label>
        <HookFormErrorMessage errors={errors} name="passwordCheck" />
      </div>

      <div>
        <label>
          <LabelText>
            <Text variant="sm-medium">휴대폰 번호 &nbsp;</Text>
            <Text variant="sm-medium" color="skyblue_500">
              *필수
            </Text>
          </LabelText>
          <Stack gap={8}>
            <Input
              type="text"
              inputMode="numeric"
              placeholder="010"
              isError={Boolean(errors.phoneNumber1)}
              {...register('phoneNumber1', {
                required: '휴대폰 번호를 입력해주세요.',
                pattern: {
                  value: /^01([0|1|6|7|8|9])$/,
                  message: '잘못된 휴대폰 번호입니다.',
                },
              })}
              width="61px"
            />
            <Input
              type="text"
              inputMode="numeric"
              placeholder="1234"
              isError={Boolean(errors.phoneNumber2)}
              {...register('phoneNumber2', {
                required: '휴대폰 번호를 입력해주세요.',
                pattern: {
                  value: /^[0-9]{3,4}$/,
                  message: '잘못된 휴대폰 번호입니다.',
                },
              })}
              width="113.5px"
            />
            <Input
              type="text"
              inputMode="numeric"
              placeholder="5678"
              isError={Boolean(errors.phoneNumber3)}
              {...register('phoneNumber3', {
                required: '휴대폰 번호를 입력해주세요.',
                pattern: {
                  value: /^[0-9]{4}$/,
                  message: '잘못된 휴대폰 번호입니다.',
                },
              })}
              width="113.5px"
            />
          </Stack>
        </label>
        {getPhoneNumberErrorMessage()}
      </div>

      <div>
        <label>
          <LabelText>
            <Text variant="sm-medium">
              어디에서 세일즈클루를 알게 되셨나요? &nbsp;
            </Text>
            <Text variant="sm-medium" color="skyblue_500">
              *필수
            </Text>
          </LabelText>
          <Styled.Select
            {...register('acquisitionPath', {
              required: '유입경로를 선택해주세요.',
            })}
            isError={Boolean(errors.acquisitionPath)}
            textVariant="sm-medium"
          >
            <option value="">-- 선택하세요 --</option>
            <option value="검색엔진">검색엔진(구글/네이버)</option>
            <option value="블로그">블로그</option>
            <option value="SNS">SNS(스레드/링크드인)</option>
            <option value="지인 추천">지인 추천</option>
            <option value="광고">광고</option>
            <option value="기타">기타</option>
          </Styled.Select>
        </label>
        <HookFormErrorMessage errors={errors} name="acquisitionPath" />
      </div>

      {acquisitionPath === '기타' && (
        <div style={{ marginTop: 8 }}>
          <label>
            <LabelText>
              <Text variant="sm-medium">기타 유입경로</Text>
            </LabelText>
            <Input
              type="text"
              placeholder="ex) 커뮤니티 이름, 지인 이름 등"
              {...register('acquisitionPathEtc', {
                required: '기타 유입경로를 입력해주세요.',
              })}
            />
          </label>
          <HookFormErrorMessage errors={errors} name="acquisitionPathEtc" />
        </div>
      )}

      <div>
        <label>
          <LabelText>회사</LabelText>
          <Input
            type="text"
            placeholder="ex) 주식회사 세일즈클루"
            isError={Boolean(errors.company)}
            {...register('company')}
          />
        </label>
      </div>

      <div>
        <label>
          <LabelText>직책</LabelText>
          <Input
            type="text"
            placeholder="ex) 세일즈 리드"
            {...register('position')}
          />
        </label>
      </div>

      <CheckBox register={register} setValue={setValue} getValues={getValues} />

      {signUpErrorMessage && (
        <Text as="p" color="cerise_300" variant="sm-regular" marginTop={8}>
          {signUpErrorMessage}
        </Text>
      )}

      <Button
        type="submit"
        variant="skyblue_400_fill"
        textVariant="lg-regular"
        width="304px"
        radius={4}
        paddingY={12}
        disabled={!isAuthCodeChecked || !isValid}
      >
        회원가입 완료
      </Button>
    </Styled.Wrapper>
  );
};

export default Form;
